// Shared injection keys used for dependency injection between parent/child components or pages
// See: https://vuejs.org/api/composition-api-dependency-injection.html
// and: https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject

import type { AsyncDataRequestStatus } from "#app";
import type { Reactive, ShallowRef, WritableComputedRef } from "vue";

export interface SearchTabsContext {
    tab: Readonly<Ref<string | number | undefined>>;
    setTab: (value: string | number) => void;
}

export const SearchInjectKey: InjectionKey<SearchTabsContext> = Symbol("SearchTabs");

export interface SearchIds {
    distributorIds: ShallowRef<Set<number>>;
    manufacturerIds: ShallowRef<Set<number>>;
    partKeys: ShallowRef<Set<string>>;
    sortOrders: ShallowRef<Set<string>>;
    clickHash: Ref<string>;
}

export const SearchIdsInjectKey: InjectionKey<SearchIds> = Symbol("SearchIds");

export interface CategoryContext {
    category: Ref<Category | undefined>;
    filterOptions: Ref<FilterOption[] | undefined>;
    filterValues: Ref<FilterValues>;
    manufacturers?: Ref<CategoryManufacturer[] | undefined>;
}

export const CategoryInjectKey: InjectionKey<CategoryContext> = Symbol("Category");

export function injectCategory() {
    return inject(CategoryInjectKey, {
        category: ref(),
        filterOptions: ref(),
        filterValues: ref({ inStock: false, manufacturers: [], specs: [] }),
        manufacturers: ref([]),
    });
}

export interface BOMContext {
    bomSearch: Ref<BOMSearch | undefined>;
    distributors: ShallowRef<BOMSearchDistributor[] | undefined>;
    isReadOnly: ComputedRef<boolean>;
    trackEvent: (eventName: string, eventParams?: Record<string, string | number | boolean>) => void;
}

export const BOMInjectKey: InjectionKey<BOMContext> = Symbol("BOM");

export function injectBOM() {
    return inject(BOMInjectKey, {
        bomSearch: ref(),
        distributors: shallowRef([]),
        isReadOnly: computed(() => false),
        trackEvent: notImplemented("trackEvent"),
    });
}

export type BomTableDragState = {
    startIndex: number;
    targetIndex: number;
    isActive: boolean;
};

export interface BOMContainerContext {
    filters: Reactive<BOMFilters>;
    removedIds: Ref<Record<number, boolean>>;
    showPriceBreaks: Ref<boolean>;
    showReferenceNumbers: Ref<boolean>;
    page: Ref<number>;
    pageSize: Ref<number>;
    openPartNotifications: (searchPart: BOMSearchPart, responsePart: BOMSearchResponsePart) => void;
    partEmissions: Ref<Record<string, number | null>>;
    totalEmissions: ComputedRef<number>;
    emissionsStatus: ComputedRef<AsyncDataRequestStatus>;
    emissionsStatuses: Ref<Record<string, AsyncDataRequestStatus>>;
    showEmissions: ComputedRef<boolean>;
    partsStatuses: Ref<Record<string, AsyncDataRequestStatus>>;
    responseParts: ShallowRef<Record<string, BOMSearchResponsePart>>;
    coverage: ComputedRef<BOMDistributorCoverage | null>;
    coverageParts: ComputedRef<Record<string, BOMCoveragePart>>;
    addPart: (part: Partial<BOMSearchPart>) => void;
    updatePart: (part: BOMSearchPart) => void;
    removePart: (part: BOMSearchPart) => void;
    selections: Ref<BOMSearchSelection[]>;
    saveSelection: (selection: BOMSearchSelection) => void;
    removeSelection: (selection: BOMSearchSelection) => void;
}

export const BOMContainerInjectKey: InjectionKey<BOMContainerContext> = Symbol("BOMContainer");

export function injectBOMContainer() {
    return inject(BOMContainerInjectKey, {
        filters: reactive<BOMFilters>({ availability: "all" }),
        removedIds: ref({}),
        showPriceBreaks: ref(false),
        showReferenceNumbers: ref(false),
        page: ref(0),
        pageSize: ref(0),
        openPartNotifications: notImplemented("openPartNotifications"),
        partEmissions: ref({}),
        totalEmissions: computed(() => 0),
        emissionsStatus: computed<AsyncDataRequestStatus>(() => "idle"),
        emissionsStatuses: ref({}),
        showEmissions: computed(() => false),
        partsStatuses: ref({}),
        responseParts: shallowRef({}),
        coverage: computed(() => null),
        coverageParts: computed(() => ({})),
        addPart: notImplemented("addPart"),
        updatePart: notImplemented("updatePart"),
        removePart: notImplemented("removePart"),
        selections: ref([]),
        saveSelection: notImplemented("saveSelection"),
        removeSelection: notImplemented("removeSelection"),
    });
}

export interface BOMTableContext {
    columnState: BomTableDragState;
    distributorColspan: ComputedRef<number>;
}

export const BOMTableInjectKey: InjectionKey<BOMTableContext> = Symbol("BOMTable");

export function injectBOMTable() {
    return inject(BOMTableInjectKey, {
        columnState: reactive<BomTableDragState>({ startIndex: -1, targetIndex: -1, isActive: false }),
        distributorColspan: computed(() => 1),
    });
}

export interface BomTableRowContext {
    searchPart: Ref<BOMSearchPart>;
    partNumber: Ref<string>;
    partManufacturer: WritableComputedRef<PartManufacturer>;
    removePart: () => void;
    cancelRemove: () => void;
}

export const BOMTableRowInjectKey: InjectionKey<BomTableRowContext> = Symbol("BOMTableRow");

export function injectBOMRow() {
    return inject(BOMTableRowInjectKey, {
        searchPart: ref({
            Id: 0,
            Key: "",
            PartNumber: "",
            PartNumberScrubbedNonMeaningful: "",
            ManufacturerId: 0,
            ManufacturerName: "",
            Quantity: 0,
        }),
        partNumber: ref(""),
        partManufacturer: computed<PartManufacturer>({
            get: () => ({
                PartId: 0,
                Key: "",
                PartNumber: "",
                PartNumberScrubbedNonMeaningful: "",
                PartClickHash: "",
                ManufacturerId: 0,
                ManufacturerName: "",
            }),
            set: () => {},
        }),
        removePart: notImplemented("removePart"),
        cancelRemove: notImplemented("cancelRemove"),
    });
}

export interface ManufacturerContext {
    manufacturer: Ref<ManufacturerDetails | undefined>;
}

export const ManufacturerInjectKey: InjectionKey<ManufacturerContext> = Symbol("Manufacturer");

export function injectManufacturer() {
    return inject(ManufacturerInjectKey, {
        manufacturer: ref(),
    });
}

export interface DistributorContext {
    distributor: Ref<DistributorDetails | undefined>;
}

export const DistributorInjectKey: InjectionKey<DistributorContext> = Symbol("Distributor");

export function injectDistributor() {
    return inject(DistributorInjectKey, {
        distributor: ref(),
    });
}

export function notImplemented(name: string) {
    return function () {
        throw new Error(name + " not implemented");
    };
}
